import {
  faCloudUpload,
  faEnvelope,
  faHome,
  faLink,
  faPaperclip,
  faPhone,
  faPlug,
  faPrint,
  faSave,
  faUser
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext } from 'react'
import { AuthenticationContext } from '../../AuthenticationContext'
import GoogleMapsEmbed from '../../components/maps/GoogleMapsEmbed'
import { LegacyJobData } from '../../domain/jobs/jobs'
import FabButton from '../../components/common/FabButton'
import { jobRouteBase } from './jobDetailModel'
import './JobHero.css'

type Props = {
  job: LegacyJobData
  isSaving: boolean
  isDirty: boolean
  onSave?: () => void
  onPrint?: () => void
  onUploadAttachment: () => void
  onLinkCustomerToQbo?: () => void
}

const JobHero = ({ job, isSaving, isDirty, onSave, onPrint, onUploadAttachment, onLinkCustomerToQbo }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const canAccessAccounting = currentUser?.roles.includes('Accounting') ?? false
  const isConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks ?? false

  const customerHref = `#!/customers#${job.customer.url}`
  const propertyHref = `#!/customers/${job.customer.url}/properties#${job.property.url}`
  const backUrl = encodeURIComponent(`#!${jobRouteBase(job.type)}/${job.url}`)
  const attachmentsHref = `#!/customers/${job.customer.id}/properties/${job.property.id}/attachments?jobId=${job.id}&backUrl=${backUrl}#${(job.defaultAttachmentFolder ?? '').toLowerCase()}`

  const qboTitle = job.customer.qboId
    ? `Linked in QuickBooks Online to Customer ${job.customer.qboDisplayName || `# ${job.customer.qboId}`}`
    : 'Link Customer in QuickBooks Online'

  return <>
    <div className="hero-container job-hero">
      <div
        className="job-hero__photo"
        style={job.primaryPhotoUrl ? { backgroundImage: `url(${job.primaryPhotoUrl})` } : undefined}
      />

      <div className="customer-property-details">
      <dl>
        <dt><a href={customerHref}><b><FontAwesomeIcon icon={faUser} /> Customer</b></a></dt>
        <dd>{job.customer.displayName}</dd>
        {job.customer.address1 && <dd>{job.customer.address1}</dd>}
        {job.customer.address2 && <dd>{job.customer.address2}</dd>}
        <dd>{job.customer.city}, {job.customer.state} {job.customer.zip}</dd>
        <dd><br /></dd>
        {(job.customer.emails ?? []).map(email => <dd key={email.id}>
          <a href={`mailto:${email.address}`}><FontAwesomeIcon icon={faEnvelope} fixedWidth /> {email.address}</a>
        </dd>)}
        <dd><br /></dd>
        {(job.customer.phones ?? []).map(phone => <dd key={phone.id}>
          <a href={`tel:${phone.number}`}><FontAwesomeIcon icon={faPhone} fixedWidth /> {phone.number}</a>
        </dd>)}
      </dl>

      <dl>
        <dt><a href={propertyHref}><b><FontAwesomeIcon icon={faHome} /> Property</b></a></dt>
        {job.property.address1 && <dd>{job.property.address1}</dd>}
        {job.property.address2 && <dd>{job.property.address2}</dd>}
        {job.property.county && <dd>{job.property.county} county</dd>}
        <dd>{job.property.city}, {job.property.state} {job.property.zip}</dd>
        {job.property.notes && <dd>
          <br />
          <p className="job-hero__notes">{job.property.notes}</p>
        </dd>}
      </dl>

        <dl className="job-hero__map">
          <GoogleMapsEmbed apiKey={window.env.GOOGLE_API_KEY} q={job.property.mapPosition ?? ''} />
        </dl>
      </div>
    </div>

    <div className="hero-details">
      <h1>
        <span>{job.name} ({job.no})</span>
        <br />
        <small>{job.customer.displayName} at {job.property.displayName}</small>
      </h1>

      <div className="fab-buttons-container">
        <FabButton color="orange" icon={faUser} title="Customer" href={customerHref} />
        <FabButton color="orange" icon={faHome} title="Property" href={propertyHref} />
        <FabButton
          color="blue"
          icon={faPaperclip}
          title="Attachments"
          href={attachmentsHref}
          badge={job.attachments}
          disabled={job.id === 0}
        />
        <FabButton
          color="blue"
          icon={faCloudUpload}
          title="Upload Attachment"
          disabled={job.id === 0}
          onClick={onUploadAttachment}
        />

        {canAccessAccounting && !isConnectedToQuickBooks && <FabButton
          icon={faPlug}
          title="Connect to QuickBooks Online Environment"
          href="#!/setup#quickbooks-connect"
        />}

        {canAccessAccounting && isConnectedToQuickBooks && <FabButton
          color={job.customer.qboId ? 'blue' : 'orange'}
          icon={faLink}
          title={qboTitle}
          onClick={onLinkCustomerToQbo}
        />}

        <FabButton icon={faPrint} title="Print" onClick={onPrint} />
        <FabButton icon={faSave} title="Save Changes" disabled={isSaving || !isDirty} onClick={onSave} />
      </div>
    </div>
  </>
}

export default JobHero
